import React, { Component } from 'react';
import { AgGridReact } from '@ag-grid-community/react';
import { Button } from 'antd';
import { Loader } from 'semantic-ui-react';
import { ACTION_STATUS } from '../../../../../utils/ActionStatus';
import '../../FundMonitoring.css';
import FormatUtil from '../../../../../utils/FormatUtil';

const columns = [
    {
        field: 'id',
        headerName: 'ID',
        width: 120,
        headerCheckboxSelection: true,
        headerCheckboxSelectionFilteredOnly: true,
        checkboxSelection: params => Boolean(params.node && !params.node.group)
    },
    { field: 'date', headerName: 'Date', width: 100, },
    { field: 'ruleId', headerName: 'Rule ID', width: 140 },
    { field: 'ruleCategory', headerName: 'Rule Category', width: 100 },
    {
        field: 'reportThreshold',
        headerName: 'Report Threshold',
        width: 110,
        valueFormatter: params => {
            const expMetric = params.data && params.data.expMetric;
            if (expMetric && expMetric.toLowerCase().includes('pct')) {
                return FormatUtil.percentValueFormatter1(params);
            }
            return FormatUtil.numValueFormatter1(params);
        }
    },
    {
        field: 'alertThreshold',
        headerName: 'Alert Threshold',
        width: 110,
        valueFormatter: params => {
            const expMetric = params.data && params.data.expMetric;
            if (expMetric && expMetric.toLowerCase().includes('pct')) {
                return FormatUtil.percentValueFormatter1(params);
            }
            return FormatUtil.numValueFormatter1(params);
        }
    },
    {
        field: 'checkResult',
        headerName: 'Check Result',
        width: 120,
        cellStyle: params => {
            const val = params.value;
            if (!val) return null;
            switch (val) {
                case 'Alert_New':
                    return { color: '#80340d', backgroundColor: '#ffff00', fontWeight: 'bold' };
                case 'Alert_Up':
                    return { color: '#e97132', backgroundColor: '#ffff00', fontWeight: 'bold' };
                case 'Alert_Down':
                    return { color: '#e97132', fontWeight: 'bold' };
                case 'Alert_NoChange':
                    return { color: '#e97132', backgroundColor: '#e8e8e8', fontWeight: 'bold' };
                case 'Report_New':
                case 'Report_Up':
                case 'Report_Down':
                    return { color: '#000000', backgroundColor: '#ff9d9d', fontWeight: 'bold' };
                case 'Report_NoChange':
                    return { color: '#000000', backgroundColor: '#e8e8e8', fontWeight: 'bold' };
                case 'Pass':
                    return { color: '#388600', fontWeight: 'bold' };
                case 'NoCountryError':
                    return { color: '#c00000', fontWeight: 'bold' };
                default:
                    if (val.startsWith('Alert')) {
                        return { color: '#e97132', fontWeight: 'bold' };
                    }
                    if (val.startsWith('Report')) {
                        return { color: '#000000', fontWeight: 'bold' };
                    }
                    return null;
            }
        }
    },
    { field: 'fund', headerName: 'Fund', width: 90 },
    { field: 'bbgTicker', headerName: 'BBG Ticker', width: 120 },
    { field: 'secName', headerName: 'Security Name', width: 260, tooltipField: 'secName' },
    { field: 'assetClass', headerName: 'Asset Class', width: 100, sort: 'asc' },
    { field: 'country', headerName: 'Country', width: 110, sort: 'desc' },
    { field: 'type', headerName: 'Type', width: 100 },
    { field: 'position', headerName: 'Position', width: 110, valueFormatter: FormatUtil.numValueFormatter1 },
    { field: 'issuedShare', headerName: 'Issued Share', width: 120, valueFormatter: FormatUtil.numValueFormatter1 },
    { field: 'issuedSharePercent', headerName: 'Issued Share %', width: 130, valueFormatter: FormatUtil.percentValueFormatter3 },
    { field: 'localCurrency', headerName: 'Local Currency', width: 100 },
    { field: 'fxRate', headerName: 'FxRate', width: 110 },
    { field: 'marketValueLocal', headerName: 'Market Value (Local)', width: 160, valueFormatter: FormatUtil.numValueFormatter1 },
    { field: 'marketValueUSD', headerName: 'Market Value (USD)', width: 160, valueFormatter: FormatUtil.numValueFormatter1 },
    { field: 'fundNavUSD', headerName: 'Fund NAV (USD)', width: 130, valueFormatter: FormatUtil.numValueFormatter1 },
    { field: 'fundNavPercent', headerName: 'Fund NAV %', width: 140, valueFormatter: FormatUtil.percentValueFormatter3 },
    {
        field: 'resolved',
        headerName: 'Resolved',
        width: 90,
        valueFormatter: params => {
            if (!params.data) return '';
            const isResolved = params.value === true || params.value === 1 || params.value === 'true' || params.value === '1';
            return isResolved ? 'Yes' : 'No';
        }
    },
    { field: 'resolvedTime', headerName: 'ResolvedTime', width: 140 },
    { field: 'resolvedBy', headerName: 'ResolvedBy', width: 120 },
    { field: 'remarks', headerName: 'Remarks', width: 220, tooltipField: 'remarks' },
    { field: 'lastModifiedDate', headerName: 'LastModifiedDate', width: 180, rowGroup: true, sort: 'desc' },
    { field: 'lastModifiedBy', headerName: 'LastModifiedBy', width: 160 },
];

class RuleReportGrid extends Component {
    onSelectionChanged = event => {
        const rowNodeArray = event.api.getSelectedNodes();
        if (this.props.selectRuleReport) {
            this.props.selectRuleReport(rowNodeArray.filter(rowNode => rowNode.data).map(rowNode => rowNode.data));
        }
    };

    normalizeRows = response => {
        const data = response && response.data;
        if (Array.isArray(data)) return data;
        if (data && Array.isArray(data.list)) return data.list;
        if (data && Array.isArray(data.data)) return data.data;
        return data && data.id !== undefined ? [data] : [];
    };

    getRuleCategory = row => {
        if (row.ruleCategory) return row.ruleCategory;
        const masters = this.props.ruleMasterActionResp && this.props.ruleMasterActionResp.data;
        const rows = Array.isArray(masters) ? masters : (masters && masters.list) || [];
        const rule = rows.find(item => item.ruleId === row.ruleId);
        return rule ? rule.category : '';
    };

    render() {
        const { loadRuleReportActionStatus, ruleReportActionResp, ruleReportQueryFields } = this.props;
        const isLoading = loadRuleReportActionStatus === ACTION_STATUS.LOGINING;
        const { showAll } = ruleReportQueryFields;
        const rows = this.normalizeRows(ruleReportActionResp)
            .map(row => ({ ...row, ruleCategory: this.getRuleCategory(row) }))
            .filter(row => showAll || (row.checkResult && (row.checkResult.startsWith('Alert') || row.checkResult.startsWith('Report') || row.checkResult === 'NoCountryError')));

        return <div className="ag-theme-balham grid-wrapper">
            {isLoading ? <Loader active content="Loading..." /> : <AgGridReact
                columnDefs={columns}
                rowData={rows}
                defaultColDef={{ sortable: true, filter: true, resizable: true, minWidth: 80 }}
                sideBar={
                    {
                        toolPanels: ['columns', 'filters'],
                        defaultToolPanel: '',
                    }
                }
                rowSelection="multiple"
                autoGroupColumnDef={{
                    headerName: 'LastModifiedDate',
                    minWidth: 150,
                    sort: 'desc',
                    cellRendererParams: {
                        checkbox: false,
                    },
                    checkboxSelection: false,
                    headerCheckboxSelection: false,
                }}
                groupDefaultExpanded={-1}
                isRowSelectable={node => !node.group}
                onSelectionChanged={this.onSelectionChanged}
                animateRows
                enableRangeSelection
                statusBar={{ statusPanels: [{ statusPanel: 'agTotalAndFilteredRowCountComponent', align: 'left' }, { statusPanel: 'agSelectedRowCountComponent' }] }}
            />}
        </div>;
    }
}

export default RuleReportGrid;

